// react
import {
	ChangeEvent,
	Dispatch,
	FormEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';
import { ContextSpendPlan } from '../../pages/ProjectV2/ProjectV2.context';

// hooks
import { useScrollToError } from '../../../hooks/useScrollToError';

// mui
import {
	Alert,
	Grid,
	InputAdornment,
	LinearProgress,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

// icons
import { CalendarMonth as IconCalendar, Description as IconDetails } from '@mui/icons-material';

// components
import { TimelinePresets, TimelineDates, parseFiscalYearStartMonth } from '../../ui/TimelinePresets/TimelinePresets';
import {
	CustomFieldsFormSection,
	validateCustomFields,
} from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';

// types
import { WorkPlanPayload, WorkPlan } from '../../../@types';

// config
import { featureFlags } from '../../../config/featureFlags';

// Props & Types

export interface FormCreateWorkPlanProps {
	workPlan?: WorkPlan | null;
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

// Component

export const FormCreateWorkPlan = ({
	workPlan,
	onClose,
	onSuccess,
	setIsLoading,
}: FormCreateWorkPlanProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { createWorkPlan, updateWorkPlan } = useContext(ContextSpendPlan);

	// derived
	const isEditMode = Boolean(workPlan);

	// session

	// state - data fetched directly
	const [error, setError] = useState<string | null>(null);

	// scroll to error when it occurs
	const errorRef = useScrollToError(error);

	// state - timeline preset selection
	// If editing with existing dates, default to 'custom' so the date fields are shown
	const [timelinePreset, setTimelinePreset] = useState(() => {
		if (workPlan?.dateStart && workPlan?.dateEnd) {
			return 'custom';
		}
		return '';
	});

	// state - values for UI logic only (derived state needs these)
	const [values, setValues] = useState(() => {
		if (workPlan) {
			// Dates may come with timestamps, slice to YYYY-MM-DD format for date inputs
			const parseDate = (dateStr: string | null | undefined): string => {
				if (!dateStr) return '';
				return dateStr.slice(0, 10);
			};
			return {
				allocatedAmount: workPlan.amount || ('' as number | ''),
				allocatedAmountLocal: workPlan.amountLocal || ('' as number | ''),
				customData: workPlan.customData || ({} as Record<string, unknown>),
				dateEnd: parseDate(workPlan.dateEnd),
				dateStart: parseDate(workPlan.dateStart),
				description: workPlan.description || '',
				name: workPlan.name || '',
			};
		}
		return {
			allocatedAmount: '' as number | '',
			allocatedAmountLocal: '' as number | '',
			customData: {} as Record<string, unknown>,
			dateEnd: '',
			dateStart: '',
			description: '',
			name: '',
		};
	});

	// Get reporting currency
	const reportingCurrency =
		project?.currencies?.find((c) => c.type === 'REPORTING')?.currency || 'USD';

	// Get local currency (if feature enabled)
	const localCurrency = project?.currencies?.find((c) => c.type === 'LOCAL')?.currency || null;
	const showLocalCurrency = featureFlags['LOCAL_CURRENCY'] && localCurrency;

	// vars
	const formId = isEditMode ? 'form-edit-work-plan' : 'form-create-work-plan';

	// loading state
	const isLoading = false;

	// effect - set loading state
	useEffect(() => {
		if (setIsLoading) {
			setIsLoading(isLoading);
		}
	}, [isLoading, setIsLoading]);

	// handlers
	const handleChangeAmount = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const numValue = value === '' ? '' : Number(value);
		setValues((prev) => ({ ...prev, allocatedAmount: numValue }));
	};

	const handleChangeAmountLocal = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const numValue = value === '' ? '' : Number(value);
		setValues((prev) => ({ ...prev, allocatedAmountLocal: numValue }));
	};

	const handleChangeValue = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues((prev) => ({ ...prev, [name]: value }));
	};

	const handleTimelineChange = (dates: TimelineDates & { presetId: string }) => {
		setTimelinePreset(dates.presetId);
		setValues((prev) => ({
			...prev,
			dateStart: dates.startDate,
			dateEnd: dates.endDate,
		}));
	};

	// handler for custom column field changes
	const handleCustomFieldChange = (columnId: string, value: unknown) => {
		setValues((prev) => ({
			...prev,
			customData: {
				...prev.customData,
				[columnId]: value,
			},
		}));
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);

		// validation
		if (!values.name || !String(values.name).trim()) {
			setError(t('spendPlan.workPlan.errorName'));
			return;
		}
		if (!values.allocatedAmount || Number(values.allocatedAmount) <= 0) {
			setError(t('spendPlan.workPlan.errorAmount'));
			return;
		}
		if (!values.dateStart || !values.dateEnd) {
			setError(t('spendPlan.workPlan.errorPeriod'));
			return;
		}
		if (new Date(values.dateStart) > new Date(values.dateEnd)) {
			setError(t('spendPlan.workPlan.errorDateOrder'));
			return;
		}

		// Validate required custom fields
		const customColumnErrors = validateCustomFields(
			(project?.customFieldDefinitions || []).find((d) => d.entityType === 'work_plan')
				?.definitions || [],
			values.customData
		);
		if (customColumnErrors.length > 0) {
			setError(t('spendPlan.workPlan.errorRequiredFields', { fields: customColumnErrors.join(', ') }));
			return;
		}

		try {
			if (setIsLoading) {
				setIsLoading(true);
			}

			const basePayload = {
				amount: Number(values.allocatedAmount),
				amountLocal:
					showLocalCurrency && values.allocatedAmountLocal
						? Number(values.allocatedAmountLocal)
						: null,
				currency: reportingCurrency,
				currencyLocal: showLocalCurrency ? localCurrency : null,
				customData: values.customData,
				dateEnd: values.dateEnd,
				dateStart: values.dateStart,
				description: values.description ? String(values.description) : null,
				name: String(values.name),
			};

			let res;
			if (isEditMode && workPlan) {
				// PATCH - don't include projectId
				res = await updateWorkPlan(workPlan.id, basePayload);
			} else {
				// POST - include projectId
				const createPayload: WorkPlanPayload = {
					...basePayload,
					projectId: project?.id as number,
				};
				res = await createWorkPlan(createPayload);
			}

			if (res.error) {
				throw new Error(res.error.message);
			}

			if (onSuccess) {
				onSuccess();
			}

			setBanner({
				hasClose: true,
				message: isEditMode ? t('spendPlan.workPlan.updatedSuccess', { name: values.name }) : t('spendPlan.workPlan.createdSuccess', { name: values.name }),
				severity: 'success',
			});

			if (onClose) {
				onClose();
			}
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: isEditMode ? t('spendPlan.workPlan.updateError') : t('spendPlan.workPlan.createError')
			);
		} finally {
			if (setIsLoading) {
				setIsLoading(false);
			}
		}
	};

	return (
		<form id={formId} noValidate onSubmit={handleSubmit}>
			{isLoading && <LinearProgress sx={{ mb: 2 }} />}

			{/* Step 1: Work Plan Details */}
			<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, mb: 3, p: 3 }}>
				<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
					<IconDetails color="primary" />
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					{t('spendPlan.workPlan.step1Title')}
					</Typography>
				</Stack>
				<Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
				{t('spendPlan.workPlan.step1Subtitle')}
			</Typography>

				<Grid container spacing={3}>
					{/* Work Plan Name */}
					<Grid item xs={12}>
						<TextField
							fullWidth
							label={t('spendPlan.workPlanName')}
							name="name"
							onChange={handleChangeValue}
							placeholder={t('spendPlan.workPlanNamePlaceholder')}
							required
							value={values.name}
						/>
					</Grid>

					{/* Planned Amount (Reporting Currency) */}
					<Grid item xs={12} md={showLocalCurrency ? 6 : 12}>
						<TextField
							fullWidth
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">{reportingCurrency}</InputAdornment>
								),
							}}
							label={t('spendPlan.workPlan.plannedAmountReporting')}
							name="allocatedAmount"
							onChange={handleChangeAmount}
							required
							type="number"
							value={values.allocatedAmount}
						/>
					</Grid>

					{/* Local Currency Amount (behind feature flag) */}
					{showLocalCurrency && (
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								InputProps={{
									startAdornment: <InputAdornment position="start">{localCurrency}</InputAdornment>,
								}}
								label={t('spendPlan.workPlan.plannedAmountLocal')}
								name="allocatedAmountLocal"
								onChange={handleChangeAmountLocal}
								type="number"
								value={values.allocatedAmountLocal}
							/>
						</Grid>
					)}

					{/* Description */}
					<Grid item xs={12}>
						<TextField
							fullWidth
							label={t('description')}
							multiline
							name="description"
							onChange={handleChangeValue}
							placeholder={t('spendPlan.workPlanDescription')}
							rows={2}
							value={values.description}
						/>
					</Grid>
				</Grid>
			</Paper>

			{/* Step 2: Planning Period (Timeline) */}
			<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 3 }}>
				<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
					<IconCalendar color="primary" />
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					{t('spendPlan.workPlan.step2Title')}
					</Typography>
				</Stack>
				<Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
					{t('spendPlan.workPlan.step2Subtitle')}
				</Typography>

				<TimelinePresets
					endDate={values.dateEnd}
					fiscalYearStartMonth={parseFiscalYearStartMonth(project?.fiscalYearStart)}
					onChange={handleTimelineChange}
					required
					selectedPreset={timelinePreset}
					startDate={values.dateStart}
				/>
			</Paper>

			{/* Custom Work Plan Fields */}
			{(() => {
				const workPlanColumns =
					(project?.customFieldDefinitions || []).find((d) => d.entityType === 'work_plan')
						?.definitions || [];
				return workPlanColumns.filter((c) => c.isActive).length > 0 ? (
					<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 3 }}>
						<Grid container spacing={2}>
							<CustomFieldsFormSection
								columns={workPlanColumns}
								customData={values.customData}
								onChange={handleCustomFieldChange}
								title={t('spendPlan.workPlan.customFields')}
							/>
						</Grid>
					</Paper>
				) : null;
			})()}

			{error && (
				<Alert ref={errorRef} severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}
		</form>
	);
};
